import { useEffect, useRef, useState } from "react";
import { Undo2, X, CircleCheck, CircleAlert } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { Effect, Match } from "effect";
import { ApiError } from "#api.ts";
import { useAuth } from "#auth.tsx";
import { runEffectMutation } from "#effect-query.ts";
import { useOrders, type ClosedOrderChange } from "#orders-context.tsx";
import { orderMutationSchema } from "#orders.ts";
import { useLanguage } from "#i18n.tsx";

/** Ten-second confirmation, with an atomic revision-checked Undo and no mutation retries. */
export function OrderClosureToast({
  change,
}: {
  readonly change: ClosedOrderChange;
}) {
  const { api } = useAuth();
  const { t, orderStatus, language } = useLanguage();
  const { merge, dismissClosure, refresh, setNotice } = useOrders();
  const started = useRef(false);
  const deadline = useRef(0);
  const [remaining, setRemaining] = useState(10);
  const undoMutation = useMutation<
    NonNullable<typeof orderMutationSchema.Type>,
    ApiError
  >({
    retry: false,
    mutationFn: () =>
      runEffectMutation(
        api
          .requestEffect({
            path: `/merchants/${encodeURIComponent(change.order.merchantId)}/orders/${encodeURIComponent(change.order.id)}`,
            method: "PUT",
            body: {
              order_state: change.previousState,
              expected_revision: change.revision,
            },
            schema: orderMutationSchema,
          })
          .pipe(
            Effect.flatMap((result) =>
              result
                ? Effect.succeed(result)
                : Effect.fail(new ApiError(404, t("toast.uncertain"))),
            ),
          ),
      ),
    onSuccess: (result) => {
      merge([result.order]);
      if (result.liveUpdatesDelayed) setNotice(t("toast.delayed"));
    },
    onError: () => {
      void refresh();
    },
  });
  const state = Match.value(undoMutation).pipe(
    Match.when({ status: "idle" }, () => ({ kind: "ready" }) as const),
    Match.when({ status: "pending" }, () => ({ kind: "undoing" }) as const),
    Match.when({ status: "error" }, ({ error }) =>
      Match.valueTags(error, {
        ApiError: (failure) =>
          ({
            kind: "result",
            failed: true,
            message:
              failure.status === 409
                ? t("toast.conflict")
                : failure.status === 0 || failure.status >= 500
                  ? t("toast.uncertain")
                  : failure.message,
          }) as const,
      }),
    ),
    Match.when(
      { status: "success" },
      () =>
        ({
          kind: "result",
          failed: false,
          message: t("toast.restored", {
            status: orderStatus(change.previousState).toLocaleLowerCase(
              language,
            ),
          }),
        }) as const,
    ),
    Match.exhaustive,
  );
  useEffect(() => {
    if (state.kind === "undoing") return;
    deadline.current = Date.now() + 10_000;
    setRemaining(10);
    const expiry = setTimeout(() => dismissClosure(change.revision), 10_000);
    const timer = setInterval(() => {
      const seconds = Math.max(
        0,
        Math.ceil((deadline.current - Date.now()) / 1000),
      );
      setRemaining(seconds);
      if (seconds === 0) dismissClosure(change.revision);
    }, 250);
    return () => {
      clearInterval(timer);
      clearTimeout(expiry);
    };
  }, [change.revision, dismissClosure, state.kind]);
  const undo = () => {
    if (started.current) return;
    if (Date.now() >= deadline.current) {
      dismissClosure(change.revision);
      return;
    }
    started.current = true;
    undoMutation.mutate();
  };
  return (
    <div
      className={`order-toast ${state.kind === "result" && state.failed ? "toast-failed" : change.order.state === "order_canceled" ? "toast-cancelled" : "toast-completed"}`}
      role={state.kind === "result" && state.failed ? "alert" : "status"}
      aria-atomic="true"
    >
      <div className="toast-message">
        {(state.kind === "result" && state.failed) ||
        change.order.state === "order_canceled" ? (
          <CircleAlert aria-hidden="true" />
        ) : (
          <CircleCheck aria-hidden="true" />
        )}
        <span>
          {Match.value(state).pipe(
            Match.when({ kind: "ready" }, () =>
              t(
                change.order.state === "order_canceled"
                  ? "toast.cancelled"
                  : "toast.completed",
                { label: change.order.label },
              ),
            ),
            Match.when({ kind: "undoing" }, () => t("toast.undoing")),
            Match.when({ kind: "result" }, ({ message }) => message),
            Match.exhaustive,
          )}
          {state.kind === "ready" && <small>{t("toast.window")}</small>}
        </span>
      </div>
      {state.kind !== "undoing" && (
        <div className="toast-countdown" aria-hidden="true">
          <svg viewBox="0 0 40 40">
            <circle className="countdown-track" cx="20" cy="20" r="17" />
            <circle
              className="countdown-progress"
              cx="20"
              cy="20"
              r="17"
              pathLength="100"
              strokeDasharray="100"
              strokeDashoffset={100 - remaining * 10}
            />
          </svg>
          <span>{remaining}</span>
        </div>
      )}
      {state.kind === "ready" && (
        <button className="secondary toast-undo" onClick={undo}>
          <Undo2 aria-hidden="true" />
          {t("toast.undo")}
        </button>
      )}
      <button
        className="icon-button"
        aria-label={t("toast.dismiss")}
        disabled={state.kind === "undoing"}
        onClick={() => dismissClosure(change.revision)}
      >
        <X />
      </button>
    </div>
  );
}
